import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  GeneralFeedbackButton,
  useAnalyticsOrgLocations,
  useTimePeriodDropDownSelector,
  findDatesRange,
} from '@frontend/analytics-shared';
import { Page } from '@frontend/page';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { AppointmentAnalyticsBreadcrumb } from '../../atoms/appointment-analytics-breadcrumb';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { useIsAppointmentDemoAccount } from '../../hooks/use-is-appointment-demo-account';
import { CompareLocationsButton } from '../compare-locations-button';
import { CompareLocationsFilters } from '../compare-locations-filters';
import { CompareLocationsTable } from '../compare-locations-table';

export const CompareLocationsView = () => {
  const { t } = useTranslation('analytics');

  const { activeTab } = useAppointmentAnalyticsV2ShallowStore('activeTab');
  const isDemoAccount = useIsAppointmentDemoAccount();

  const { preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });

  const { defaultStartDate: pastStartDate, defaultEndDate: pastEndDate } = useTimePeriodDropDownSelector({
    defaultPeriod: 'last30Days',
  });

  const upcomingDateRange = useMemo(() => findDatesRange({ offset: 7, range: 'day', type: 'future' }), []);

  const [localFilters, setLocalFilters] = useState<{
    locationId?: string[];
    startDate?: string;
    endDate?: string;
  }>({
    locationId: preselectedAccessibleLocationIds,
    startDate: undefined,
    endDate: undefined,
  });

  const [filterHintText, setFilterHintText] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (preselectedAccessibleLocationIds?.length && !initialized) {
      if (activeTab === APPOINTMENT_TABS.UPCOMING) {
        setLocalFilters({
          locationId: preselectedAccessibleLocationIds,
          startDate: dayjs(upcomingDateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          endDate: dayjs(upcomingDateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        });
      } else {
        setLocalFilters({
          locationId: preselectedAccessibleLocationIds,
          startDate: dayjs(pastStartDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          endDate: dayjs(pastEndDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        });
      }
      setInitialized(true);
    }
  }, [preselectedAccessibleLocationIds, pastStartDate, pastEndDate, upcomingDateRange, activeTab, initialized]);

  return (
    <Page>
      <Page.Header>
        <AppointmentAnalyticsBreadcrumb />
        <Page.Header.Heading>
          <Page.Header.Title
            title={
              <Heading level={1} css={{ display: 'inline-flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                {t('Appointment Analytics')}
                <Text css={{ fontSize: theme.fontSize(20) }}>- {t('Location comparison')}</Text>
              </Heading>
            }
          />
          <Page.Header.Subtitle
            subtitle={t('Showing results for {{filtersInfo}}.', {
              filtersInfo: filterHintText,
            })}
          />
          <Page.Header.Action>
            <div
              css={{
                display: 'flex',
                gap: theme.spacing(3),
                alignItems: 'center',
              }}
            >
              <GeneralFeedbackButton selectedLocationFromPageFilter={localFilters.locationId} />
              <CompareLocationsButton />
            </div>
          </Page.Header.Action>
        </Page.Header.Heading>
      </Page.Header>
      <Page.Body>
        <div css={styles.bodyContent}>
          <CompareLocationsFilters
            filters={localFilters}
            onChange={setLocalFilters}
            setFilterHintText={setFilterHintText}
          />
          <CompareLocationsTable filters={initialized ? localFilters : {}} />
        </div>
      </Page.Body>
    </Page>
  );
};

const styles = {
  bodyContent: css`
    padding-top: ${theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
};
